import React, { FC, useCallback, useEffect, useState } from "react";

import { OverlayTrigger, Table, Tooltip } from "react-bootstrap";

import classNames from "classnames";
import currency from "currency.js";
import { isNil } from "lodash";
import { SetterOrUpdater, useRecoilState } from "recoil";

import { PaymentStatuses, ResaleFragment, ResaleStatuses } from "@api/graphql";
import { RESALE_STATUS_SORT_ORDER } from "@common/sort_order_enum";
import ParticipantName from "@components/account_profile/ParticipantName";
import { ExternalRoles } from "@roles";
import { SelectedResalesType, selectedResales } from "@states/resale_submission";
import { formatISODateToFormat } from "@util/index";

import { ResaleStatusDropDown } from "../ResaleStatusDropDown";
import { useIsAuthUserAllowed } from "../permission_control/ProtectedComponent";

import { PaymentStatusBadge } from "./PaymentStatusBadge";
import { ResaleStatusBadge } from "./ResaleStatusBadge";
import { TableContents, TableHeader } from "./TableContents";
import { TableHeaders as CustomTableHeaders } from "./TableHeaders";

import styles from "./ProvisionalResaleTable.module.scss";

export enum ResaleStatusComponentType {
  BADGE = "Badge",
  DROPDOWN = "Dropdown",
}

const DEFAULT_TABLE_HEADERS: TableHeader<ResaleFragment>[] = [
  { label: "ID", accessor: "resale_id" },
  { label: "Artwork title", accessor: "artwork_name" },
  { label: "Artist", accessor: "artist_name" },
  { label: "Sold date", accessor: "date_sold" },
  { label: "Resale price", accessor: "resale_price" },
  { label: "Royalty", accessor: "royalty" },
  { label: "Resale status", accessor: "resale_status" },
];
interface ProvisionalResaleTableProps {
  resales: ResaleFragment[];
  refetch?: () => void;
  onRowClick?: (rowData: ResaleFragment) => void;
  customTableHeaders?: TableHeader<ResaleFragment>[];
  customTableStyleClassName?: string;
  showCheckBox?: boolean;
  rowCheckBoxIsDisabled?: (row: ResaleFragment) => boolean;
  handleHeaderCheckBoxChange?: (
    setRecoilSelectedRows: SetterOrUpdater<SelectedResalesType>,
    resales: ResaleFragment[],
  ) => (e: React.ChangeEvent<HTMLInputElement>, batchID: string) => void;
}

const WithTooltip = ({ children, label }: { children: React.ReactElement; label: string }) => {
  return (
    <OverlayTrigger placement="top-start" overlay={<Tooltip>{label}</Tooltip>}>
      {children}
    </OverlayTrigger>
  );
};

const updateSelectedRows = (prevSelectedRows: SelectedResalesType, row: ResaleFragment): SelectedResalesType => {
  const {
    batch_grouping_id: batchId,
    resale_id: rowId,
    invoice: rowInvoice,
    invoice_line: rowInvoiceLine,
    artist_id: artistId,
  } = row;
  const currentSelectedBatch = prevSelectedRows[batchId];
  const invoice =
    rowInvoice && rowInvoiceLine
      ? {
          invoice: rowInvoice,
          invoice_line: rowInvoiceLine,
        }
      : undefined;

  if (currentSelectedBatch) {
    const currentSelectedResales = currentSelectedBatch.map((resale) => resale.resaleId);
    // handle uncheck
    if (currentSelectedResales.includes(rowId)) {
      const toReturn = {
        ...prevSelectedRows,
        [batchId]: currentSelectedBatch.filter(({ resaleId }) => resaleId !== rowId),
      };
      if (toReturn[batchId].length === 0) {
        delete toReturn[batchId];
      }
      return toReturn;
    } else {
      // handle check
      return {
        ...prevSelectedRows,
        [batchId]: [...currentSelectedBatch, { resaleId: rowId, invoice, artistId }],
      };
    }
  } else {
    return {
      ...prevSelectedRows,
      [batchId]: [{ resaleId: rowId, invoice, artistId }],
    };
  }
};

export const ProvisionalResaleTable: FC<ProvisionalResaleTableProps> = ({
  resales,
  refetch,
  onRowClick,
  customTableHeaders,
  customTableStyleClassName,
  showCheckBox,
  rowCheckBoxIsDisabled,
  handleHeaderCheckBoxChange,
}) => {
  const [sortKey, setSortKey] = useState<keyof ResaleFragment>("resale_status");
  const [sortDirection, setSortDirection] = useState<"ascending" | "descending">("ascending");
  const [sortedData, setSortedData] = useState(resales);
  const [dropdownToOpenById, setDropdownToOpenById] = useState<string | undefined>(undefined);

  const [recoilSelectedRows, setRecoilSelectedRows] = useRecoilState(selectedResales);

  const isExternalUser = useIsAuthUserAllowed(ExternalRoles);

  const handleOnDropdownClick = useCallback(
    (id: string | undefined) => {
      id === dropdownToOpenById ? setDropdownToOpenById(undefined) : setDropdownToOpenById(id);
    },
    [dropdownToOpenById],
  );

  const mapColumnValue = useCallback(
    (value: ResaleFragment[keyof ResaleFragment], key: keyof ResaleFragment, row?: ResaleFragment) => {
      // convert nullish value to "", Boolean("") => false
      const text = isNil(value) ? "" : value;

      // default component
      // TODO: [NRRS-1003] extract components and the whole if statement
      let component = (
        <td className="align-content-center" key={String(key)}>
          <span>{text}</span>
        </td>
      );

      if (row) {
        const { resale_id, artist_id, resale_status_reason, vendor_id } = row;

        if (key === "resale_id") {
          component = (
            <td className="align-content-center" key={String(key)}>
              <WithTooltip label={text.toString()}>
                <span>{text}</span>
              </WithTooltip>
            </td>
          );
        } else if (key === "artwork_name") {
          component = (
            <td className={classNames("align-content-center text-truncate")} key={String(key)}>
              <WithTooltip label={text.toString()}>
                <span>{text}</span>
              </WithTooltip>
            </td>
          );
        } else if (key === "artist_name") {
          component = (
            <td className="align-content-center" key={String(key)}>
              <ParticipantName name={text} participantId={artist_id} />
            </td>
          );
        } else if (key === "royalty" || key === "resale_price") {
          component = (
            <td className="align-content-center" key={String(key)}>
              <div>{currency(text as number).format()}</div>
            </td>
          );
        } else if (key === "resale_status") {
          const resaleStatusHeader = customTableHeaders?.find(({ accessor }) => accessor === "resale_status");
          const resaleStatusComponentType = resaleStatusHeader?.componentType;

          switch (resaleStatusComponentType) {
            case ResaleStatusComponentType.DROPDOWN:
              component = (
                <td className={classNames(styles["keep-opaque"], "align-content-center")} key={String(key)}>
                  <div className={classNames("b5", "w-100")}>
                    {text && resale_id && (
                      <ResaleStatusDropDown
                        onSuccess={refetch}
                        resaleId={resale_id}
                        vendorId={vendor_id}
                        initialStatus={value as ResaleStatuses}
                        id={resale_id.toString()}
                        isResaleLinkedToAnArtist={Boolean(artist_id)}
                        open={dropdownToOpenById === resale_id.toString()}
                        onDropdownClick={handleOnDropdownClick}
                      />
                    )}
                  </div>
                </td>
              );
              break;

            default:
              component = (
                <td className="align-content-center" key={String(key)}>
                  <div
                    className={classNames(
                      styles["badge-cell"],
                      styles["badge-truncate"],
                      "align-content-center d-flex",
                    )}
                  >
                    <ResaleStatusBadge
                      cellValue={value as ResaleStatuses}
                      resaleStatusReason={resale_status_reason}
                      isExternalUser={isExternalUser}
                    />
                  </div>
                </td>
              );
              break;
          }
        } else if (key === "payment_status") {
          component = (
            <td className="align-content-center" key={String(key)}>
              <div
                className={classNames(styles["badge-cell"], styles["badge-truncate"], "align-content-center d-flex")}
              >
                <PaymentStatusBadge cellValue={value as PaymentStatuses} isExternalUser={isExternalUser} />
              </div>
            </td>
          );
        } else if (key === "date_sold") {
          component = (
            <td className="align-content-center" key={String(key)}>
              <div>{value && formatISODateToFormat({ isoDate: value as string, targetDateFormat: "DD/MM/YYYY" })}</div>
            </td>
          );
        }
      }

      return { component };
    },

    [customTableHeaders, dropdownToOpenById, handleOnDropdownClick, isExternalUser, refetch],
  );

  // Effect only applies to when the resale is updated
  // apply the current sortKey and current sortDirection to the updated resale data
  useEffect(() => {
    sortByKey(sortKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resales]); // keep this dependency as resales, the sortByKey function will handle data mutation when sort is changed

  const sortByKey = useCallback(
    (key: keyof ResaleFragment) => {
      setSortKey(key);

      const getStatusRank = (status: ResaleStatuses) => RESALE_STATUS_SORT_ORDER[status];

      let direction: "ascending" | "descending" = "ascending";

      if (key === sortKey) {
        direction = sortDirection === "ascending" ? "descending" : "ascending";
      }
      setSortDirection(direction);

      const sortedData = [...resales].sort((a, b) => {
        if (key === "resale_status") {
          // Handle undefined resale_status by giving it the lowest priority (highest rank number)
          const rankA = getStatusRank(a.resale_status ?? ResaleStatuses.INVALID);
          const rankB = getStatusRank(b.resale_status ?? ResaleStatuses.INVALID);

          // Adjust sorting based on the direction
          if (direction === "ascending") {
            return rankA - rankB; // Sort by rank in ascending order
          }
          return rankB - rankA; // Sort by rank in descending order
        }

        // If not sorting by resale_status, fall back to default sorting
        if (direction === "ascending") {
          return a[key]! > b[key]! ? 1 : -1;
        }
        return a[key]! < b[key]! ? 1 : -1;
      });

      setSortedData(sortedData);
    },
    [resales, sortDirection, sortKey],
  );

  const handleCheckBoxChange = useCallback(
    (row: ResaleFragment) => {
      setRecoilSelectedRows((prevSelectedRows) => {
        return updateSelectedRows(prevSelectedRows, row);
      });
    },
    [setRecoilSelectedRows],
  );

  const checkRowIsChecked = useCallback(
    (row: ResaleFragment) => {
      const resaleIds = recoilSelectedRows[row.batch_grouping_id]?.map(({ resaleId }) => resaleId);
      return resaleIds?.includes(row.resale_id);
    },
    [recoilSelectedRows],
  );

  return (
    <>
      <Table borderless hover className={customTableStyleClassName}>
        <CustomTableHeaders
          headers={customTableHeaders ?? DEFAULT_TABLE_HEADERS}
          customStyles={styles}
          sortColumn={sortKey}
          setSortColumn={sortByKey}
          showHeaderCheckBox={!!handleHeaderCheckBoxChange}
          tableRowsContainCheckBox={showCheckBox}
          batchID={sortedData[0].batch_grouping_id}
          handleHeaderCheckBoxChange={handleHeaderCheckBoxChange?.(setRecoilSelectedRows, resales)}
        />

        <TableContents<ResaleFragment>
          headers={customTableHeaders ?? DEFAULT_TABLE_HEADERS}
          data={sortedData}
          keyAccessor="resale_id"
          customStyles={styles}
          mapColumnValue={mapColumnValue}
          onRowClick={onRowClick}
          isRowDisabled={(row) => row.resale_status === ResaleStatuses.PROVISIONALLY_INELIGIBLE}
          isRowChecked={(row) => checkRowIsChecked(row)}
          isRowCheckBoxDisabled={(row) => (rowCheckBoxIsDisabled ? rowCheckBoxIsDisabled(row) : false)}
          handleCheckBoxChange={handleCheckBoxChange}
          showCheckBox={showCheckBox}
        />
      </Table>
    </>
  );
};
